import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DollarSign,
  Clock,
  CreditCard,
  CheckCircle,
  MessageSquare,
  ArrowRight,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';

const statusColors = {
  pending_approval: 'bg-amber-100 text-amber-800',
  approved: 'bg-blue-100 text-blue-800',
  deposit_paid: 'bg-emerald-100 text-emerald-800',
  fully_paid: 'bg-green-100 text-green-800',
  expired: 'bg-gray-100 text-gray-600',
  rejected: 'bg-red-100 text-red-700',
  cancelled: 'bg-red-50 text-red-600',
};

const statusLabels = {
  pending_approval: 'Pendiente',
  approved: 'Aprobada',
  deposit_paid: 'Apartado',
  fully_paid: 'Pagada',
  expired: 'Expirada',
  rejected: 'Rechazada',
  cancelled: 'Cancelada',
};

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/admin/dashboard');
      setData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#5C4033]" />
      </div>
    );
  }

  const stats = [
    {
      title: 'Ingresos del Mes',
      value: formatCurrency(data?.monthly_revenue || 0),
      icon: DollarSign,
      color: 'bg-green-500',
    },
    {
      title: 'Por Aprobar',
      value: data?.pending_approval || 0,
      icon: Clock,
      color: 'bg-amber-500',
    },
    {
      title: 'Esperando Pago',
      value: data?.awaiting_payment || 0,
      icon: CreditCard,
      color: 'bg-blue-500',
    },
    {
      title: 'Apartados Pagados',
      value: data?.deposit_paid || 0,
      icon: CheckCircle,
      color: 'bg-emerald-500',
    },
    {
      title: 'Consultas Pendientes',
      value: data?.pending_inquiries || 0,
      icon: MessageSquare,
      color: 'bg-purple-500',
    },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-serif text-3xl text-[#1A1A1A] mb-8">Dashboard</h1>

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-8">
        {stats.map((stat) => (
          <Card key={stat.title} className="border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 mb-1">{stat.title}</p>
                  <p className="text-2xl font-serif text-[#1A1A1A]">{stat.value}</p>
                </div>
                <div className={`w-10 h-10 ${stat.color} rounded-xl flex items-center justify-center`}>
                  <stat.icon className="h-5 w-5 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Reservations */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-serif text-xl">Reservaciones Recientes</CardTitle>
          <Link
            to="/admin/reservaciones"
            className="text-[#5C4033] hover:text-[#C8962D] text-sm flex items-center"
          >
            Ver Todas <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
        </CardHeader>
        <CardContent>
          {data?.recent_reservations?.length > 0 ? (
            <div className="space-y-3">
              {data.recent_reservations.map((reservation) => (
                <div
                  key={reservation.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-sm text-[#1A1A1A]">{reservation.user_name}</p>
                    <p className="text-xs text-gray-500">
                      {formatDate(reservation.check_in)}
                      {reservation.check_out && ` - ${formatDate(reservation.check_out)}`}
                    </p>
                  </div>
                  <div className="text-right flex items-center gap-3">
                    <p className="font-serif text-sm text-[#5C4033]">
                      {formatCurrency(reservation.total)}
                    </p>
                    <Badge className={`text-xs ${statusColors[reservation.status] || 'bg-gray-100 text-gray-600'}`}>
                      {statusLabels[reservation.status] || reservation.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No hay reservaciones recientes</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
